import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { DiaExpediente, PausaAutomatica, getPausasVigentes } from '../api/pausasAutomaticasApi';
import { queryKeys } from '../infrastructure/react-query/queryKeys';

/**
 * Bloqueio das ações de atividade na tela do colaborador (at-48b, ampliado na at-53).
 *
 * Duas razões independentes para bloquear: uma **janela de pausa** em vigor (almoço, lanche) ou a
 * oficina **fora do expediente**. Três cuidados que fazem este hook ser confiável:
 *
 * 1. **Relógio do servidor**: a rota devolve `agora`, e todo cálculo usa `Date.now() + offset`.
 *    PC da oficina com hora errada não trava (nem deixa de travar) fora de hora.
 * 2. **Timer na borda exata**: agenda um `setTimeout` para o instante em que a janela abre/fecha
 *    ou o expediente vira, em vez de ficar checando de N em N segundos. Deu 12:00, a tela reage
 *    no segundo. A meia-noite também é borda: é quando o dia da semana muda e, com ele, toda a
 *    configuração (domingo fechado → segunda abre às 09:00).
 * 3. **Fuso da oficina**: dia da semana e hora do dia são lidos em America/Sao_Paulo via `Intl`,
 *    replicando a convenção do backend — nunca `getDay()`/`getHours()` do fuso do dispositivo.
 *
 * Falha de rede ou 403 = **fail-open** (nada trava): errar para destravado é melhor do que
 * parar a oficina por um bug de tela — o desconto do tempo acontece no fechamento de qualquer
 * forma (ADR 0023/0024), então a contabilidade não depende deste bloqueio.
 */

const FUSO_OFICINA = 'America/Sao_Paulo';

/** Sem borda adiante hoje, recheca daqui a algumas horas em vez de agendar timeout gigante. */
const RECHECAGEM_MAXIMA_MS = 6 * 60 * 60 * 1000;

const NOMES_DOS_DIAS = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
];

export interface JanelaEmVigor {
  id: string;
  nome: string;
  horaInicio: string;
  horaFim: string;
}

/** Por que a oficina está fora de operação agora (at-53). */
export interface ForaDoExpediente {
  /** `false` = dia fechado por inteiro; `true` = dia aberto, mas fora do horário. */
  diaAberto: boolean;
  /** Frase pronta para a tela ("A oficina está fechada hoje (domingo)"). */
  descricao: string;
}

interface PartesLocais {
  diaSemana: number;
  minutosDoDia: number;
  /** Meia-noite local do dia corrente, em epoch ms — âncora para calcular as bordas. */
  inicioDoDiaMs: number;
}

/**
 * Dia da semana (0 = domingo), minutos desde a meia-noite e o instante da meia-noite local,
 * tudo no fuso da oficina.
 */
export function partesLocais(agoraMs: number, timeZone: string = FUSO_OFICINA): PartesLocais {
  const formatador = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const partes: Record<string, string> = {};
  for (const parte of formatador.formatToParts(new Date(agoraMs))) {
    if (parte.type !== 'literal') partes[parte.type] = parte.value;
  }

  const diasCurtos = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const diaSemana = Math.max(diasCurtos.indexOf(partes.weekday), 0);

  // `hour12: false` devolve 24 na meia-noite em algumas implementações.
  const hora = Number(partes.hour) === 24 ? 0 : Number(partes.hour);
  const minuto = Number(partes.minute);
  const segundo = Number(partes.second);

  const minutosDoDia = hora * 60 + minuto;
  const msDesdeMeiaNoite = ((hora * 60 + minuto) * 60 + segundo) * 1000;

  return {
    diaSemana,
    minutosDoDia,
    inicioDoDiaMs: agoraMs - msDesdeMeiaNoite,
  };
}

/**
 * Qual janela está em vigor agora e quando é a próxima borda (abertura ou fechamento).
 *
 * Função pura, separada do hook de propósito: é a regra, e é onde um erro sairia caro.
 */
export function calcularJanelaEmVigor(
  agoraMs: number,
  janelas: PausaAutomatica[],
  timeZone: string = FUSO_OFICINA,
): { ativa: JanelaEmVigor | null; proximaBordaMs: number | null } {
  if (!janelas?.length) return { ativa: null, proximaBordaMs: null };

  const { diaSemana, minutosDoDia, inicioDoDiaMs } = partesLocais(agoraMs, timeZone);

  let ativa: JanelaEmVigor | null = null;
  const bordasFuturas: number[] = [];

  for (const janela of janelas) {
    if (!janela.diasSemana?.includes(diaSemana)) continue;
    if (janela.horaFimMinutos <= janela.horaInicioMinutos) continue;

    const dentro =
      minutosDoDia >= janela.horaInicioMinutos && minutosDoDia < janela.horaFimMinutos;

    if (dentro && !ativa) {
      ativa = {
        id: janela.id,
        nome: janela.nome,
        horaInicio: janela.horaInicio,
        horaFim: janela.horaFim,
      };
    }

    for (const minuto of [janela.horaInicioMinutos, janela.horaFimMinutos]) {
      if (minuto > minutosDoDia) {
        bordasFuturas.push(inicioDoDiaMs + minuto * 60 * 1000);
      }
    }
  }

  const proximaBordaMs = bordasFuturas.length ? Math.min(...bordasFuturas) : null;
  return { ativa, proximaBordaMs };
}

/** "09:00" a partir de minutos desde a meia-noite. */
function formatarMinutos(minutos: number): string {
  const normalizado = ((Math.trunc(minutos) % 1440) + 1440) % 1440;
  const horas = String(Math.floor(normalizado / 60)).padStart(2, '0');
  const restante = String(normalizado % 60).padStart(2, '0');
  return `${horas}:${restante}`;
}

/**
 * A oficina está fora do expediente agora? (at-53)
 *
 * Função pura, como a das janelas. Regra que se repete do backend: **dia sem configuração não
 * restringe nada** — sem isso, ligar a feature travaria a tela de todo mundo até o ADMIN
 * preencher os 7 dias.
 */
export function calcularForaDoExpediente(
  agoraMs: number,
  expediente: DiaExpediente[],
  timeZone: string = FUSO_OFICINA,
): { fora: ForaDoExpediente | null; proximaBordaMs: number | null } {
  if (!expediente?.length) return { fora: null, proximaBordaMs: null };

  const { diaSemana, minutosDoDia, inicioDoDiaMs } = partesLocais(agoraMs, timeZone);
  const config = expediente.find((dia) => dia.diaSemana === diaSemana);
  if (!config) return { fora: null, proximaBordaMs: null };

  // A meia-noite sempre é borda quando há expediente configurado: às 00:00 o dia da semana muda e
  // com ele a regra inteira (domingo fechado → segunda abre às 09:00).
  const meiaNoiteSeguinte = inicioDoDiaMs + 1440 * 60 * 1000;
  const bordas: number[] = [meiaNoiteSeguinte];

  if (!config.aberto) {
    return {
      fora: {
        diaAberto: false,
        descricao: `A oficina está fechada hoje (${NOMES_DOS_DIAS[diaSemana] ?? 'hoje'})`,
      },
      proximaBordaMs: meiaNoiteSeguinte,
    };
  }

  const abertura = config.aberturaMinutos;
  const fechamento = config.fechamentoMinutos;
  // Configuração incompleta não restringe (espelha o backend).
  if (abertura === null || abertura === undefined || fechamento === null || fechamento === undefined) {
    return { fora: null, proximaBordaMs: null };
  }

  for (const minuto of [abertura, fechamento]) {
    if (minuto > minutosDoDia) bordas.push(inicioDoDiaMs + minuto * 60 * 1000);
  }
  const proximaBordaMs = Math.min(...bordas);

  if (minutosDoDia < abertura) {
    return {
      fora: { diaAberto: true, descricao: `A oficina abre às ${formatarMinutos(abertura)}` },
      proximaBordaMs,
    };
  }

  if (minutosDoDia >= fechamento) {
    return {
      fora: { diaAberto: true, descricao: `A oficina fechou às ${formatarMinutos(fechamento)}` },
      proximaBordaMs,
    };
  }

  return { fora: null, proximaBordaMs };
}

export function usePausaAutomaticaAtiva(enabled: boolean): {
  janelaAtiva: JanelaEmVigor | null;
  foraDoExpediente: ForaDoExpediente | null;
} {
  const { data } = useQuery({
    queryKey: queryKeys.pausasAutomaticas.vigentes,
    queryFn: getPausasVigentes,
    enabled,
    staleTime: 5 * 60 * 1000,
    // Rede de segurança caso o websocket não esteja de pé.
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const janelas = useMemo(() => data?.janelas ?? [], [data]);
  const expediente = useMemo(() => data?.expediente ?? [], [data]);

  // Desvio entre o relógio local e o do servidor, medido na resposta.
  const offsetMs = useMemo(() => {
    if (!data?.agora) return 0;
    const servidor = Date.parse(data.agora);
    return Number.isNaN(servidor) ? 0 : servidor - Date.now();
  }, [data]);

  const [janelaAtiva, setJanelaAtiva] = useState<JanelaEmVigor | null>(null);
  const [foraDoExpediente, setForaDoExpediente] = useState<ForaDoExpediente | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const avaliar = useCallback(() => {
    if (!enabled || (!janelas.length && !expediente.length)) {
      setJanelaAtiva(null);
      setForaDoExpediente(null);
      return;
    }

    const agoraMs = Date.now() + offsetMs;
    const { ativa, proximaBordaMs: bordaJanela } = calcularJanelaEmVigor(agoraMs, janelas);
    const { fora, proximaBordaMs: bordaExpediente } = calcularForaDoExpediente(agoraMs, expediente);

    setJanelaAtiva((anterior) => (anterior?.id === ativa?.id ? anterior : ativa));
    setForaDoExpediente((anterior) =>
      anterior?.descricao === fora?.descricao ? anterior : fora,
    );

    if (timerRef.current) clearTimeout(timerRef.current);

    // A primeira borda das duas fontes manda: a que vier antes reavalia tudo.
    const bordas = [bordaJanela, bordaExpediente].filter((b): b is number => b !== null);
    const proximaBordaMs = bordas.length ? Math.min(...bordas) : null;

    // +1s de folga para o disparo cair depois da borda, nunca em cima dela.
    const esperaMs =
      proximaBordaMs !== null
        ? Math.min(Math.max(proximaBordaMs - agoraMs + 1000, 1000), RECHECAGEM_MAXIMA_MS)
        : RECHECAGEM_MAXIMA_MS;

    timerRef.current = setTimeout(avaliar, esperaMs);
  }, [enabled, janelas, expediente, offsetMs]);

  useEffect(() => {
    avaliar();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [avaliar]);

  // Máquina que dormiu e acordou dentro da janela: o timer agendado ficou defasado, então
  // reavalia ao voltar o foco/visibilidade em vez de esperar o disparo antigo.
  useEffect(() => {
    if (!enabled) return;

    const reavaliar = () => {
      if (document.visibilityState === 'visible') avaliar();
    };

    document.addEventListener('visibilitychange', reavaliar);
    window.addEventListener('focus', reavaliar);
    return () => {
      document.removeEventListener('visibilitychange', reavaliar);
      window.removeEventListener('focus', reavaliar);
    };
  }, [enabled, avaliar]);

  return { janelaAtiva, foraDoExpediente };
}
